/**
 * HomeButtonGhostModel — renders a transparent ghost circle for home button(s).
 *
 * Rendering rules:
 * - previewMap !== null AND updating: show ALL features (merge preview + Redux)
 * - previewMap !== null AND NOT updating: show only the dragged feature (preview entries)
 * - previewMap === null: show all from Redux (worker update, feature creation)
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewHomeButtonSnapshot, subscribePreviewHomeButton} from '../previewHomeButton'
import {
  getRemovedFrontFeatureSnapshot,
  subscribeRemovedFrontFeature,
  clearRemovedFrontFeature,
} from '../removedFrontFeatureStore'
import type {RemovedHomeButtonGhost} from '../removedFrontFeatureStore'
import type {HomeButtonFeature} from '../../types'
import {FRONT_FACE_EXTRUDE} from '../../workerGeo/helpers'
import {GHOST_COLOR, REMOVED_COLOR, GHOST_OPACITY} from './ghostStyle'

const Z_OFFSET = 0.15

// ── Single home button ghost ─────────────────────────────────────

interface SingleGhostProps {
  radius: number
  x: number
  y: number
  ghostZ: number
  color?: number
}

const SingleHomeButtonGhost = ({radius, x, y, ghostZ, color = GHOST_COLOR}: SingleGhostProps) => {
  const prevGeoRef = useRef<THREE.CircleGeometry | null>(null)
  const geo = useMemo(() => new THREE.CircleGeometry(radius, 32), [radius])

  useEffect(() => {
    if (prevGeoRef.current && prevGeoRef.current !== geo) {
      prevGeoRef.current.dispose()
    }
    prevGeoRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  return (
    <mesh geometry={geo} position={[x, y, ghostZ]}>
      <meshBasicMaterial color={color} transparent opacity={GHOST_OPACITY} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  )
}

// ── Container ────────────────────────────────────────────────────

const HomeButtonGhostModel = ({updating}: {updating: boolean}) => {
  const previewMap = useSyncExternalStore(subscribePreviewHomeButton, getPreviewHomeButtonSnapshot)
  const removedAllMap = useSyncExternalStore(subscribeRemovedFrontFeature, getRemovedFrontFeatureSnapshot)

  const frontFaceFeatures = useAppSelector((s) => selectActiveScene(s).frontFaceFeatures)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)

  const homeButtons = useMemo(
    () => frontFaceFeatures.filter((f): f is HomeButtonFeature => f.type === 'homeButton'),
    [frontFaceFeatures],
  )

  // Filter removed entries to only home buttons
  const removedHomeButtons = useMemo(() => {
    if (!removedAllMap) return []
    return Object.entries(removedAllMap).filter(
      (entry): entry is [string, RemovedHomeButtonGhost] => entry[1].type === 'homeButton',
    )
  }, [removedAllMap])

  // Clear removed store when worker finishes
  const prevUpdatingRef = useRef(updating)
  useEffect(() => {
    if (prevUpdatingRef.current && !updating && removedHomeButtons.length > 0) {
      clearRemovedFrontFeature()
    }
    prevUpdatingRef.current = updating
  }, [updating, removedHomeButtons.length])

  const frontZ = deviceDepth / 2
  const ffExtrude = FRONT_FACE_EXTRUDE
  const ghostZ = frontZ + ffExtrude / 2 + Z_OFFSET

  // Three rendering modes:
  // 1. previewMap + updating: show ALL (dragged from preview, others from Redux — concurrent edit)
  // 2. previewMap + !updating: show only dragged feature (preview entries only)
  // 3. no previewMap: show all from Redux (worker update / creation)
  if (previewMap && !updating) {
    // Only the feature being dragged + removed ghosts
    return (
      <group>
        {Object.entries(previewMap).map(([id, p]) => (
          <SingleHomeButtonGhost key={id} radius={p.radius} x={p.x} y={p.y} ghostZ={ghostZ} />
        ))}
        {removedHomeButtons.map(([id, r]) => (
          <SingleHomeButtonGhost
            key={`removed-${id}`}
            radius={r.radius}
            x={r.x}
            y={r.y}
            ghostZ={ghostZ}
            color={REMOVED_COLOR}
          />
        ))}
      </group>
    )
  }

  // All features — from Redux with preview overrides
  return (
    <group>
      {homeButtons.map((hb) => {
        const p = previewMap?.[hb.id]
        return (
          <SingleHomeButtonGhost
            key={hb.id}
            radius={p?.radius ?? hb.radius}
            x={p?.x ?? hb.x}
            y={p?.y ?? hb.y}
            ghostZ={ghostZ}
          />
        )
      })}
      {removedHomeButtons.map(([id, r]) => (
        <SingleHomeButtonGhost
          key={`removed-${id}`}
          radius={r.radius}
          x={r.x}
          y={r.y}
          ghostZ={ghostZ}
          color={REMOVED_COLOR}
        />
      ))}
    </group>
  )
}

export default HomeButtonGhostModel
